export type ListingCategory = 'sealed' | 'singles' | 'slabs' | 'lots' | 'accessories';

// Singles conditions (for raw cards) - includes 'sealed' for sealed promo cards
export type SinglesCondition = 'sealed' | 'mint' | 'near_mint' | 'lightly_played' | 'moderately_played' | 'heavily_played' | 'damaged';

// Sealed product conditions
export type SealedCondition = 'factory_sealed' | 'factory_sealed_minor' | 'factory_sealed_major' | 'resealed' | 'opened';

// Accessory conditions (simpler scale)
export type AccessoryCondition = 'new' | 'like_new' | 'good' | 'fair';

// Legacy union type for backwards compatibility
export type ListingCondition = SinglesCondition | SealedCondition | AccessoryCondition;

export type ListingStatus = 'active' | 'sold' | 'reserved' | 'deleted';

export interface ListingImage {
  id: string;
  listing_id: string;
  image_url: string;
  display_order: number;
  created_at: string;
}

export interface ListingSeller {
  user_id: string;
  username: string | null;
  avatar_url: string | null;
  city: string | null;
  state_code: string | null;
  seller_rating: number | null;
  total_sales: number | null;
}

export interface Listing {
  id: string;
  seller_id: string;
  title: string;
  description: string | null;
  price_cents: number;
  category: ListingCategory;
  condition: ListingCondition;
  city: string;
  state_code: string;
  is_negotiable: boolean;
  accepts_trades: boolean;
  status: ListingStatus;
  created_at: string;
  updated_at: string;
  expires_at: string;
  // Joined data
  images?: ListingImage[];
  seller?: ListingSeller;
}

// Singles condition labels
export const SINGLES_CONDITION_LABELS: Record<SinglesCondition, string> = {
  sealed: 'Sealed (Plastic)',
  mint: 'Mint',
  near_mint: 'Near Mint',
  lightly_played: 'Lightly Played',
  moderately_played: 'Moderately Played',
  heavily_played: 'Heavily Played',
  damaged: 'Damaged',
};

// Sealed condition labels
export const SEALED_CONDITION_LABELS: Record<SealedCondition, string> = {
  factory_sealed: 'Factory Sealed (Mint)',
  factory_sealed_minor: 'Factory Sealed (Minor Damage)',
  factory_sealed_major: 'Factory Sealed (Major Damage)',
  resealed: 'Resealed',
  opened: 'Opened',
};

// Accessory condition labels
export const ACCESSORY_CONDITION_LABELS: Record<AccessoryCondition, string> = {
  new: 'New',
  like_new: 'Like New',
  good: 'Good',
  fair: 'Fair',
};

// Legacy combined labels for backwards compatibility
export const CONDITION_LABELS: Record<ListingCondition, string> = {
  ...SINGLES_CONDITION_LABELS,
  ...SEALED_CONDITION_LABELS,
  ...ACCESSORY_CONDITION_LABELS,
};

export const CATEGORY_LABELS: Record<ListingCategory, string> = {
  sealed: 'Sealed',
  singles: 'Singles',
  slabs: 'Slabs',
  lots: 'Lots',
  accessories: 'Accessories',
};

// Extended category labels for wantlist (includes combined options)
export const WANTLIST_CATEGORY_LABELS: Record<string, string> = {
  ...CATEGORY_LABELS,
  singles_or_slabs: 'Singles or Slabs',
};

// Lot type definitions
export type LotType = 'singles_lot' | 'sealed_lot' | 'mixed_lot';

export const LOT_TYPE_LABELS: Record<LotType, string> = {
  singles_lot: 'Singles Lot',
  sealed_lot: 'Sealed Lot',
  mixed_lot: 'Mixed Lot',
};

// Sealed product type labels for filtering
export const PRODUCT_TYPE_LABELS: Record<string, string> = {
  booster_box: 'Booster Box',
  booster_pack: 'Booster Pack',
  booster_case: 'Booster Case',
  etb: 'Elite Trainer Box',
  premium_collection: 'Premium Collection',
  tin: 'Tin / Gift Set',
  build_battle: 'Build & Battle',
  starter_deck: 'Starter Deck',
  bundle: 'Bundle / Box Set',
  other: 'Other',
};

// Accessory type labels
export type AccessoryType = 'playmat' | 'sleeves' | 'deck_box' | 'binder' | 'toploader' | 'display_case' | 'storage' | 'dice' | 'other';

export const ACCESSORY_TYPE_LABELS: Record<AccessoryType, string> = {
  playmat: 'Playmat',
  sleeves: 'Sleeves',
  deck_box: 'Deck Box',
  binder: 'Binder',
  toploader: 'Toploaders / Holders',
  display_case: 'Display Case',
  storage: 'Storage Box',
  dice: 'Dice / Counters',
  other: 'Other Accessory',
};

// Condition abbreviations for compact display
export const CONDITION_ABBREVIATIONS: Record<ListingCondition, string> = {
  sealed: 'S',
  mint: 'M',
  near_mint: 'NM',
  lightly_played: 'LP',
  moderately_played: 'MP',
  heavily_played: 'HP',
  damaged: 'DMG',
  factory_sealed: 'FS',
  factory_sealed_minor: 'FS-',
  factory_sealed_major: 'FS--',
  resealed: 'RS',
  opened: 'O',
  new: 'New',
  like_new: 'LN',
  good: 'Good',
  fair: 'Fair',
};

export const CONDITION_COLORS: Record<ListingCondition, string> = {
  sealed: 'bg-info/20 text-info border-info/30',
  mint: 'bg-success/20 text-success border-success/30',
  near_mint: 'bg-success/20 text-success border-success/30',
  lightly_played: 'bg-caution/20 text-caution border-caution/30',
  moderately_played: 'bg-warning/20 text-warning border-warning/30',
  heavily_played: 'bg-destructive/20 text-destructive border-destructive/30',
  damaged: 'bg-destructive/20 text-destructive border-destructive/30',
  factory_sealed: 'bg-success/20 text-success border-success/30',
  factory_sealed_minor: 'bg-caution/20 text-caution border-caution/30',
  factory_sealed_major: 'bg-warning/20 text-warning border-warning/30',
  resealed: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  opened: 'bg-destructive/20 text-destructive border-destructive/30',
  new: 'bg-success/20 text-success border-success/30',
  like_new: 'bg-success/20 text-success border-success/30',
  good: 'bg-caution/20 text-caution border-caution/30',
  fair: 'bg-warning/20 text-warning border-warning/30',
};
